<template>
  <div class="ui-wysiwyg" :class="{ 'editor-focused': isFocused }" @click="setFocus">
    <v-skeleton-loader v-if="!editor" type="card" />

    <div v-else>
      <label v-if="label" class="ui-label" :for="name">
        {{ label }}
        <span v-if="required" class="ui-required">*</span>
      </label>

      <v-card class="ui-input-field">
        <!-- Editor content area -->
        <editor-content v-if="editor" :editor="editor" @input="updateValue" @focus="handleFocus" @blur="handleBlur" />

        <div id="ui-wysiwyg-editor-toolbar" :editor="editor" :tippy-options="{
    duration: [0, 1000],
    interactive: true,
    trigger: 'manual',
    hideOnClick: true,
    delay: [0, 2000],
  }" v-if="editor && show_toolbar">
          <v-menu class="ui-menu kyano-menu">
            <template v-slot:activator="{ props }">
              <v-btn v-bind="props" flat density="comfortable" icon title="Opmaak">
                <i class="fal fa-text-height"></i>
              </v-btn>
            </template>
            <v-list>
              <ui-list-item v-for="option in headingOptions" :key="option.level" @click="setHeading(option.level)"
                :class="{
    'is-active': editor.isActive(
      option.type,
      option.level ? { level: option.level } : {}
    ),
  }" :title="option.title">
              </ui-list-item>
              <ui-list-item @click="setParagraph()" title="Paragraaf"> </ui-list-item>
            </v-list>
          </v-menu>

          <v-divider vertical></v-divider>
          <v-btn flat density="comfortable" icon @click="toggleBold" :class="{ 'is-active': editor.isActive('bold') }"
            text>
            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#1f1f1f">
              <path
                d="M356.62-230q-17.62 0-29.58-11.96-11.96-11.96-11.96-29.58v-416.92q0-17.62 11.96-29.58Q339-730 356.62-730H483q57.31 0 101.15 36.15Q628-657.69 628-599.77q0 38.69-20.31 67.73-20.31 29.04-50.31 42.58 36.54 10.23 62.04 42.92t25.5 78.85q0 64.38-48.07 101.04Q548.77-230 491-230H356.62Zm9.46-47.38h122.46q46.46 0 75.04-27.97 28.57-27.96 28.57-65.11 0-37.16-28.57-65.12-28.58-27.96-75.73-27.96H366.08v186.16Zm0-232.62h114.54q39.92 0 67.61-24.69 27.69-24.69 27.69-61.85 0-37.84-28.15-62.08-28.15-24.23-66.69-24.23h-115V-510Z" />
            </svg>
          </v-btn>
          <v-btn flat density="comfortable" icon @click="toggleItalic"
            :class="{ 'is-active': editor.isActive('italic') }" text>
            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#1f1f1f">
              <path
                d="M253.85-230q-9.37 0-15.84-6.49-6.47-6.49-6.47-15.88 0-9.4 6.47-15.82 6.47-6.43 15.84-6.43h123.07l139.23-410.76H393.08q-9.37 0-15.84-6.49-6.47-6.49-6.47-15.89 0-9.39 6.47-15.82 6.47-6.42 15.84-6.42h287.69q9.37 0 15.84 6.49 6.47 6.49 6.47 15.88 0 9.4-6.47 15.82-6.47 6.43-15.84 6.43H562.31L423.08-274.62h118.46q9.37 0 15.84 6.49 6.47 6.49 6.47 15.89 0 9.39-6.47 15.82-6.47 6.42-15.84 6.42H253.85Z" />
            </svg>
          </v-btn>
          <v-divider vertical></v-divider>
          <!-- Text Color -->
          <v-menu v-model="colorMenu" attach="body" open-on-hover :close-on-content-click="false"
            transition="slide-y-reverse-transition" offset-y min-width="200" position="top">
            <template v-slot:activator="{ props }">
              <v-btn flat density="comfortable" icon v-bind="props" title="Text color">
                <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px"
                  fill="#1f1f1f">
                  <path
                    d="M152.31 0q-13.93 0-23.12-9.19Q120-18.38 120-32.31v-73.84q0-13.93 9.19-23.12 9.19-9.19 23.12-9.19h655.38q13.93 0 23.12 9.19 9.19 9.19 9.19 23.12v73.84q0 13.93-9.19 23.12Q821.62 0 807.69 0H152.31Zm128.07-280q-11.46 0-17.65-9.12-6.19-9.11-2.04-19.57l196-479.77q1.93-4.77 6.23-8.16 4.31-3.38 9.85-3.38h14.46q5.77 0 9.96 3.38 4.19 3.39 6.12 8.16l195.46 477.69q4.15 11.23-2.42 21-6.58 9.77-18.04 9.77-7.08 0-12.93-3.77-5.84-3.77-8.53-10.85l-52.08-130.92H352.62l-52.54 131.92q-2.7 6.31-7.66 9.97-4.96 3.65-12.04 3.65Zm86.39-184h223.38L482-736h-5.54L366.77-464Z" />
                </svg>
              </v-btn>
            </template>

            <!-- Color Picker -->
            <v-color-picker v-model="newcolor" dot-size="28" mode="hexa" width="200"
              @update:modelValue="setTextColor" />
          </v-menu>
          <v-menu v-model="highlightMenu" attach="body" open-on-hover :close-on-content-click="false"
            transition="slide-y-reverse-transition" offset-y min-width="200" position="top">
            <template v-slot:activator="{ props }">
              <v-btn flat density="comfortable" icon v-bind="props" title="Highlight achtergrondkleur">
                <ui-icon name="palette" />
              </v-btn>
            </template>

            <v-color-picker v-model="highlightColor" dot-size="28" mode="hexa" width="200"
              @update:modelValue="setHighlightColor" />
          </v-menu>

          <v-divider vertical></v-divider>
          <!-- List Controls -->
          <v-btn flat density="comfortable" icon @click="toggleBulletList"
            :class="{ 'is-active': editor.isActive('bulletList') }" text>
            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#1f1f1f">
              <path
                d="M404.62-220q-8.5 0-14.25-5.76t-5.75-14.27q0-8.51 5.75-14.24t14.25-5.73H780q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-220H404.62Zm0-240q-8.5 0-14.25-5.76t-5.75-14.27q0-8.51 5.75-14.24t14.25-5.73H780q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-460H404.62Zm0-240q-8.5 0-14.25-5.76t-5.75-14.27q0-8.51 5.75-14.24t14.25-5.73H780q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-700H404.62ZM213.08-186.92q-21.9 0-37.49-15.59Q160-218.11 160-240t15.59-37.49q15.59-15.59 37.49-15.59 21.89 0 37.48 15.59 15.59 15.6 15.59 37.49t-15.59 37.49q-15.59 15.59-37.48 15.59Zm0-240q-21.9 0-37.49-15.59Q160-458.11 160-480t15.59-37.49q15.59-15.59 37.49-15.59 21.89 0 37.48 15.59 15.59 15.6 15.59 37.49t-15.59 37.49q-15.59 15.59-37.48 15.59Zm0-240q-21.9 0-37.49-15.59Q160-698.11 160-720t15.59-37.49q15.59-15.59 37.49-15.59 21.89 0 37.48 15.59 15.59 15.6 15.59 37.49t-15.59 37.49q-15.59 15.59-37.48 15.59Z" />
            </svg>
          </v-btn>
          <v-btn flat density="comfortable" icon @click="toggleOrderedList"
            :class="{ 'is-active': editor.isActive('orderedList') }" text>
            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#1f1f1f">
              <path
                d="M177.69-120q-7.66 0-12.68-5-5.01-5.01-5.01-12.66t5.01-12.69q5.02-5.03 12.68-5.03H260V-210h-42.31q-7.66 0-12.68-5-5.01-5.01-5.01-12.66t5.01-12.69q5.02-5.03 12.68-5.03H260V-300h-82.31q-7.66 0-12.68-5-5.01-5.01-5.01-12.66t5.01-12.69q5.02-5.03 12.68-5.03h90q11.77 0 19.73 7.96 7.96 7.96 7.96 19.73v55.38q0 11.77-7.96 19.73-7.96 7.96-19.73 7.96 11.77 0 19.73 7.97 7.96 7.96 7.96 19.73v49.23q0 11.77-7.96 19.73-7.96 7.96-19.73 7.96h-90Zm6.93-252.31q-9.93 0-17.27-7.34Q160-387 160-396.92V-470q0-11.77 7.96-19.73 7.96-7.96 19.73-7.96H260v-54.62h-82.31q-7.66 0-12.68-5-5.01-5-5.01-12.65 0-7.66 5.01-12.69 5.02-5.04 12.68-5.04h90q11.77 0 19.73 7.96 7.96 7.96 7.96 19.73v70q0 11.77-7.96 19.73-7.96 7.96-19.73 7.96h-72.31v54.62h82.31q7.67 0 12.68 5t5.01 12.65q0 7.66-5.01 12.69-5.01 5.04-12.68 5.04h-93.07Zm53.11-252.31q-7.65 0-12.69-5.01-5.04-5.01-5.04-12.68v-162.31h-42.31q-7.66 0-12.68-5-5.01-5-5.01-12.65t5.01-12.69q5.02-5.04 12.68-5.04h53.85q9.92 0 16.88 6.96 6.96 6.96 6.96 16.89v173.84q0 7.67-5 12.68t-12.65 5.01ZM404.62-220q-8.5 0-14.25-5.76t-5.75-14.27q0-8.51 5.75-14.24t14.25-5.73H780q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-220H404.62Zm0-240q-8.5 0-14.25-5.76t-5.75-14.27q0-8.51 5.75-14.24t14.25-5.73H780q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-460H404.62Zm0-240q-8.5 0-14.25-5.76t-5.75-14.27q0-8.51 5.75-14.24t14.25-5.73H780q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-700H404.62Z" />
            </svg>
          </v-btn>
          <v-divider vertical></v-divider>
          <v-btn flat density="comfortable" icon @click="liftListItem"
            :disabled="!editor.can().liftListItem('listItem')" text>
            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#1f1f1f">
              <path
                d="M180-160q-8.5 0-14.25-5.76T160-180.03q0-8.51 5.75-14.24T180-200h600q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-160H180Zm320-150q-8.5 0-14.25-5.76T480-330.03q0-8.51 5.75-14.24T500-350h280q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-310H500Zm0-150q-8.5 0-14.25-5.76T480-480.03q0-8.51 5.75-14.24T500-500h280q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-460H500Zm0-150q-8.5 0-14.25-5.76T480-630.03q0-8.51 5.75-14.24T500-650h280q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-610H500ZM180-760q-8.5 0-14.25-5.76T160-780.03q0-8.51 5.75-14.24T180-800h600q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-760H180Zm64.46 364.46-72.77-72.77q-5.23-4.95-5.23-11.55 0-6.6 5.23-11.83l72.77-72.77q7.69-7.69 17.77-3.82 10.08 3.88 10.08 14.74v147.08q0 10.86-10.08 14.74-10.08 3.87-17.77-3.82Z" />
            </svg>
          </v-btn>
          <v-btn flat density="comfortable" icon @click="sinkListItem"
            :disabled="!editor.can().sinkListItem('listItem')" text>
            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#1f1f1f">
              <path
                d="M180-160q-8.5 0-14.25-5.76T160-180.03q0-8.51 5.75-14.24T180-200h600q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-160H180Zm320-150q-8.5 0-14.25-5.76T480-330.03q0-8.51 5.75-14.24T500-350h280q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-310H500Zm0-150q-8.5 0-14.25-5.76T480-480.03q0-8.51 5.75-14.24T500-500h280q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-460H500Zm0-150q-8.5 0-14.25-5.76T480-630.03q0-8.51 5.75-14.24T500-650h280q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-610H500ZM180-760q-8.5 0-14.25-5.76T160-780.03q0-8.51 5.75-14.24T180-800h600q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-760H180Zm7.85 364.46q-7.7 7.69-17.77 3.82Q160-395.6 160-406.46v-147.08q0-10.86 10.08-14.74 10.07-3.87 17.77 3.82l72.77 72.77q5.23 4.95 5.23 11.55 0 6.6-5.23 11.83l-72.77 72.77Z" />
            </svg>
          </v-btn>

          <v-btn v-show="isFocused" class="ui-btn ui-btn-accent-primary ml-auto"
            @click.stop="removeFocus">Opslaan</v-btn>
        </div>

        <v-btn v-show="!isFocused" class="btn-edit-note ui-btn ui-btn-accent-secondary"
          @click.stop="setFocus">Wijzigen</v-btn>
      </v-card>
      <div class="ui-hint">
        <slot name="hint"></slot>
      </div>
    </div>
  </div>
</template>

<script>
import BulletList from "@tiptap/extension-bullet-list";
import Document from "@tiptap/extension-document";
import ListItem from "@tiptap/extension-list-item";
import OrderedList from "@tiptap/extension-ordered-list";
import Paragraph from "@tiptap/extension-paragraph";
import Heading from "@tiptap/extension-heading";
import Dropcursor from "@tiptap/extension-dropcursor";
import Text from "@tiptap/extension-text";
import Placeholder from "@tiptap/extension-placeholder";
import Bold from "@tiptap/extension-bold";
import Italic from "@tiptap/extension-italic";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import HardBreak from "@tiptap/extension-hard-break";

import { BubbleMenu, Editor, EditorContent } from "@tiptap/vue-3";
import editorutil from "@/util/EditorUtils.js";

export default {
  props: {
    editorContent: { type: String, default: "" },
    rows: { type: Number, default: 10 },
    label: { type: String, default: "" },
    show_toolbar: { type: Boolean, default: true },
    show_mail_templates: { type: Boolean, default: false },
    editorPadding: { type: String, default: "10px" },
    placeholder: { type: String, default: "Start met typen..." },
    errorMessages: { type: String, default: "" },
  },
  data() {
    return {
      overlay: false,
      isFocused: false,
      activatorRef: null,
      provider: null,
      room: null,
      mail_template_dialog: false,
      debounceTimer: null,
      editor: null,
      newcolor: "#000000",
      highlightMenu: false,
      highlightColor: "#ffff00",
      colorMenu: false,
      editorStyle: {
        minHeight: `${this.rows * 24}px`,
        height: `100%`,
        display: "flex",
        flexDirection: "column",
        padding: this.editorPadding,
      },

      headingOptions: [
        {
          level: 1,
          title: "Heading 1",
          icon:
            '<svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#1f1f1f"><path d="M180-160q-8.5 0-14.25-5.76T160-180.03q0-8.51 5.75-14.24T180-200h600q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-160H180Zm320-150q-8.5 0-14.25-5.76T480-330.03q0-8.51 5.75-14.24T500-350h280q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-310H500Zm0-150q-8.5 0-14.25-5.76T480-480.03q0-8.51 5.75-14.24T500-500h280q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-460H500Zm0-150q-8.5 0-14.25-5.76T480-630.03q0-8.51 5.75-14.24T500-650h280q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-610H500ZM180-760q-8.5 0-14.25-5.76T160-780.03q0-8.51 5.75-14.24T180-800h600q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-760H180Zm64.46 364.46-72.77-72.77q-5.23-4.95-5.23-11.55 0-6.6 5.23-11.83l72.77-72.77q7.69-7.69 17.77-3.82 10.08 3.88 10.08 14.74v147.08q0 10.86-10.08 14.74-10.08 3.87-17.77-3.82Z"/></svg>',
          type: "heading",
        },
        {
          level: 2,
          title: "Heading 2",
          icon:
            '<svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#1f1f1f"><path d="M179.97-300q-8.51 0-14.24-5.75T160-320v-320q0-8.5 5.76-14.25t14.27-5.75q8.51 0 14.24 5.75T200-640v140h200v-140q0-8.5 5.76-14.25t14.27-5.75q8.51 0 14.24 5.75T440-640v320q0 8.5-5.76 14.25T419.97-300q-8.51 0-14.24-5.75T400-320v-140H200v140q0 8.5-5.76 14.25T179.97-300Zm372.34 0q-13.73 0-23.02-9.29T520-332.31v-103.07q0-26.66 18.98-45.64T584.62-500h150.76q10.77 0 17.7-6.92 6.92-6.93 6.92-17.7v-70.76q0-10.77-6.92-17.7-6.93-6.92-17.7-6.92H540q-8.5 0-14.25-5.76T520-640.03q0-8.51 5.75-14.24T540-660h195.38q26.66 0 45.64 18.98T800-595.38v70.76q0 26.66-18.98 45.64T735.38-460H584.62q-10.77 0-17.7 6.92-6.92 6.93-6.92 17.7V-340h220q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-300H552.31Z"/></svg>',
          type: "heading",
        },
        {
          level: 3,
          title: "Heading 3",
          icon:
            '<svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#1f1f1f"><path d="M180-160q-8.5 0-14.25-5.76T160-180.03q0-8.51 5.75-14.24T180-200h600q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-160H180Zm320-150q-8.5 0-14.25-5.76T480-330.03q0-8.51 5.75-14.24T500-350h280q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-310H500Zm0-150q-8.5 0-14.25-5.76T480-480.03q0-8.51 5.75-14.24T500-500h280q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-460H500Zm0-150q-8.5 0-14.25-5.76T480-630.03q0-8.51 5.75-14.24T500-650h280q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-610H500ZM180-760q-8.5 0-14.25-5.76T160-780.03q0-8.51 5.75-14.24T180-800h600q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-760H180Zm64.46 364.46-72.77-72.77q-5.23-4.95-5.23-11.55 0-6.6 5.23-11.83l72.77-72.77q7.69-7.69 17.77-3.82 10.08 3.88 10.08 14.74v147.08q0 10.86-10.08 14.74-10.08 3.87-17.77-3.82Z"/></svg>',
          type: "heading",
        },
        {
          level: 4,
          title: "Heading 4",
          icon:
            '<svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#1f1f1f"><path d="M180-160q-8.5 0-14.25-5.76T160-180.03q0-8.51 5.75-14.24T180-200h600q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-160H180Zm320-150q-8.5 0-14.25-5.76T480-330.03q0-8.51 5.75-14.24T500-350h280q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-310H500Zm0-150q-8.5 0-14.25-5.76T480-480.03q0-8.51 5.75-14.24T500-500h280q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-460H500Zm0-150q-8.5 0-14.25-5.76T480-630.03q0-8.51 5.75-14.24T500-650h280q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-610H500ZM180-760q-8.5 0-14.25-5.76T160-780.03q0-8.51 5.75-14.24T180-800h600q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-760H180Zm64.46 364.46-72.77-72.77q-5.23-4.95-5.23-11.55 0-6.6 5.23-11.83l72.77-72.77q7.69-7.69 17.77-3.82 10.08 3.88 10.08 14.74v147.08q0 10.86-10.08 14.74-10.08 3.87-17.77-3.82Z"/></svg>',
          type: "heading",
        },
        {
          level: 5,
          title: "Heading 5",
          icon:
            '<svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#1f1f1f"><path d="M180-160q-8.5 0-14.25-5.76T160-180.03q0-8.51 5.75-14.24T180-200h600q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-160H180Zm320-150q-8.5 0-14.25-5.76T480-330.03q0-8.51 5.75-14.24T500-350h280q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-310H500Zm0-150q-8.5 0-14.25-5.76T480-480.03q0-8.51 5.75-14.24T500-500h280q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-460H500Zm0-150q-8.5 0-14.25-5.76T480-630.03q0-8.51 5.75-14.24T500-650h280q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-610H500ZM180-760q-8.5 0-14.25-5.76T160-780.03q0-8.51 5.75-14.24T180-800h600q8.5 0 14.25 5.76t5.75 14.27q0 8.51-5.75 14.24T780-760H180Zm64.46 364.46-72.77-72.77q-5.23-4.95-5.23-11.55 0-6.6 5.23-11.83l72.77-72.77q7.69-7.69 17.77-3.82 10.08 3.88 10.08 14.74v147.08q0 10.86-10.08 14.74-10.08 3.87-17.77-3.82Z"/></svg>',
          type: "heading",
        },
      ],
    };
  },
  components: {
    EditorContent,
    BubbleMenu,
  },
  mounted() {
    this.initializeEditor();
    this.handleeditorContent(this.editorContent);
    this.activatorRef = this.$refs.card;
  },
  beforeUnmount() {
    if (this.editor) {
      this.editor.destroy();
    }
    if (this.provider) {
      this.provider.destroy(); // Close the connection correctly
    }
  },
  watch: {
    editorContent(newValue) {
      setTimeout(() => {
        if (this.editor && newValue !== this.editor.getHTML()) {
          this.editor.commands.setContent(newValue);
        }
      }, 100);
    },
  },
  methods: {
    initializeEditor() {
      this.editor = new Editor({
        extensions: [
          Document,
          Paragraph,
          Text,
          BulletList,
          OrderedList,
          ListItem,
          Bold,
          Italic,
          Color,
          Highlight.configure({
            multicolor: true, // laat meerdere kleuren toe
          }),
          TextStyle,
          Dropcursor,
          HardBreak,
          Heading.configure({ levels: [1, 2, 3, 4, 5, 6] }),
          Placeholder.configure({
            placeholder: this.placeholder, // Placeholder text
          }),
        ],
        content: this.editorContent || "",
        onUpdate: () => {
          this.debounceUpdateSettings();
        },
        editorProps: {
          handleKeyDown: (view, event) => {
            if (event.shiftKey && event.key === "Enter") {
              event.preventDefault(); // Prevent default behavior
              this.editor.chain().focus().setHardBreak().run(); // Use HardBreak command
              return true;
            }
            return false;
          },
        },
      });
    },
    setFocus() {
      this.editor.commands.focus(); // Handmatig focus instellen
      this.isFocused = true;
    },

    removeFocus() {
      this.editor.commands.blur();
      this.isFocused = false;
    },
    handleeditorContent(newContent) {
      if (this.editor && this.editor.getHTML() !== newContent) {
        this.editor.commands.setContent(newContent); // Update editor content when prop changes
      }
    },
    debounceUpdateSettings() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer); // Clear the previous timeout if any
      }
      this.debounceTimer = setTimeout(() => {
        this.$emit("update:modelValue", this.editor.getHTML());
      }, 1000);
    },
    executeCommandWrapper(command) {
      editorutil.executeCommand(command);
    },
    setFormatBlockWrapper(format) {
      editorutil.setFormatBlock(format);
    },
    toggleBulletList() {
      this.editor.chain().focus().toggleBulletList().run();
    },
    toggleOrderedList() {
      this.editor.chain().focus().toggleOrderedList().run();
    },
    sinkListItem() {
      this.editor.chain().focus().sinkListItem("listItem").run();
    },
    liftListItem() {
      this.editor.chain().focus().liftListItem("listItem").run();
    },
    setParagraph() {
      this.editor.chain().focus().setParagraph().run();
    },
    setHeading(level) {
      this.editor.chain().focus().toggleHeading({ level }).run();
    },
    toggleBold() {
      this.editor.chain().focus().toggleBold().run();
    },
    toggleItalic() {
      this.editor.chain().focus().toggleItalic().run();
    },
    setTextColor(color) {
      this.editor.chain().focus().setColor(color).run();
    },
    setHighlightColor(color) {
      if (this.editor) {
        this.editor.chain().focus().setHighlight({ color }).run();
      }
    },
    inserttemplatecontent(content) {
      console.log("Insert Template Content:", content);
      this.editor.chain().focus().insertContent(content).run();
      this.mail_template_dialog = false; // Close the dialog after insertion
    },
    closedialogmailtemplate(value) {
      this.mail_template_dialog = value; // To close the dialog
    },
  },
};
</script>
